import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { VERSION_LABEL } from '../version';
import { 
  LayoutDashboard, Settings, Users, Trophy, CalendarDays,
  Key, FileText, Target, Menu, X, LogOut, ChevronRight,
  Award, Star, IndianRupee, ShieldCheck, UserCircle, BarChart2, Radio
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import useAuthStore from '@/stores/authStore';

const generalNavItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/tournaments', label: 'Tournaments', icon: Trophy },
  { path: '/admin/teams', label: 'Teams', icon: Users },
  { path: '/admin/rankings', label: 'Rankings', icon: BarChart2 },
  { path: '/admin/player-scores', label: 'Player Scores', icon: Users },
  { path: '/admin/sponsors', label: 'Sponsors', icon: Award },
  { path: '/admin/auction', label: 'Auction', icon: IndianRupee },
  { path: '/admin/scoreboard', label: 'Scoreboard', icon: Radio },
];

const adminNavItems = [
  { path: '/admin/players', label: 'Members', icon: Users },
  { path: '/admin/player-categories', label: 'Categories', icon: Star },
  { path: '/admin/scorers', label: 'Scorers', icon: Target },
  { path: '/admin/tournament-setup', label: 'Bulk Upload', icon: Settings },
  { path: '/admin/matches', label: 'Matches', icon: CalendarDays },
  { path: '/admin/api-keys', label: 'API Keys', icon: Key },
  { path: '/admin/audit', label: 'Audit Log', icon: FileText },
];

const NavItem = ({ item, onClick }) => (
  <NavLink
    key={item.path}
    to={item.path}
    end={item.exact}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
        isActive
          ? 'bg-[rgba(245,197,24,0.10)] text-[#f5c518] border-l-2 border-[#f5c518] -ml-[2px] pl-[14px]'
          : 'text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26]'
      }`
    }
    data-testid={`admin-sidebar-nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <item.icon className="h-5 w-5" />
    {item.label}
  </NavLink>
);

const SidebarNav = ({ onItemClick }) => {
  const isAdmin  = useAuthStore(state => state.isAdmin)();
  const isScorer = useAuthStore(state => state.isScorer)();
  const isGuest  = useAuthStore(state => state.isGuest);
  return (
    <nav className="px-3 space-y-1">
      {/* My Profile — hidden for guests */}
      {!isGuest && (
      <NavLink
        to="/admin/profile"
        onClick={onItemClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
            isActive
              ? 'bg-[rgba(245,197,24,0.10)] text-[#f5c518] border-l-2 border-[#f5c518] -ml-[2px] pl-[14px]'
              : 'text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26]'
          }`
        }
        data-testid="admin-sidebar-nav-link-my-profile"
      >
        <UserCircle className="h-5 w-5" />
        My Profile
      </NavLink>
      )}

      {!isGuest && <Separator className="my-2 bg-[#252a37]" />}

      {/* General section — visible to all */}
      {generalNavItems.map(item => <NavItem key={item.path} item={item} onClick={onItemClick}/>)}

      {/* Administration — admins only */}
      {isAdmin && (
        <>
          <Separator className="my-3 bg-[#252a37]" />
          <p className="px-3 pt-1 pb-1 text-[10px] font-bold text-[#8a93a3] uppercase tracking-widest flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3"/>Administration
          </p>
          {adminNavItems.map(item => <NavItem key={item.path} item={item} onClick={onItemClick}/>)}
        </>
      )}

      <Separator className="my-3 bg-[#252a37]" />

      {/* Scorer Portal — shown for admins AND users with scorer role */}
      {(isAdmin || isScorer) && (
        <NavLink
          to="/score"
          onClick={onItemClick}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26] transition-colors duration-150"
          data-testid="admin-sidebar-nav-link-scorer"
        >
          <Target className="h-5 w-5" />
          Scorer Portal
          <ChevronRight className="h-4 w-4 ml-auto" />
        </NavLink>
      )}
    </nav>
  );
};

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, forceMemberView, setForceMemberView, getTournamentAdminRole } = useAuthStore();
  const navigate = useNavigate();
  const isRealAdmin = useAuthStore(state => {
    const u = state.user;
    if (!u) return false;
    if (u.super_admin) return true;
    const m = u.memberships?.find(m => m.organization_id === state.activeOrgId || m.slug === state.activeOrgSlug);
    if (['org_owner','org_admin','tournament_manager'].some(r => m?.roles?.includes(r))) return true;
    return (u.tournament_admin_roles||[]).length > 0;
  });

  // Tournament-scoped admin: auto-redirect to their tournament
  useEffect(() => {
    if (!user) return;
    const taRole = getTournamentAdminRole?.();
    const isOrgAdmin = user.super_admin || user.memberships?.some(m =>
      ['org_owner','org_admin','tournament_manager'].some(r => m?.roles?.includes(r))
    );
    if (taRole && !isOrgAdmin && window.location.pathname === '/admin') {
      navigate(`/admin/tournaments/${taRole.tournament_id}`, { replace: true });
    }
  }, [user]); // eslint-disable-line

  const handleLogout = () => { logout(); navigate('/login'); };

  const toggleMemberView = () => {
    const next = !forceMemberView;
    setForceMemberView(next);
    navigate(next ? '/admin/profile' : '/admin', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#06080d]" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-[260px] lg:flex-col lg:fixed lg:inset-y-0 border-r border-[#252a37] bg-[#10131c]">
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-[#252a37]">
          <span className="wordmark text-xl">BTCCP</span>
        </div>
        
        {/* Nav */}
        <ScrollArea className="flex-1 py-4">
          <SidebarNav />
        </ScrollArea>
        
        {/* User section */}
        <div className="p-4 border-t border-[#252a37]">
          {/* View as Member toggle — only for real admins */}
          {isRealAdmin && (
            <button onClick={toggleMemberView}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold mb-3 transition-colors ${
                forceMemberView
                  ? 'bg-[rgba(245,197,24,0.12)] text-[#f5c518] border border-[rgba(245,197,24,0.3)]'
                  : 'bg-[#161a26] text-[#8a93a3] hover:text-[#f0f2f7] border border-[#252a37]'
              }`}
              data-testid="toggle-member-view-btn"
              title={forceMemberView ? 'Switch back to admin view' : 'Preview what members see'}>
              {forceMemberView
                ? <><ShieldCheck className="h-3.5 w-3.5" /> Back to Admin View</>
                : <><UserCircle className="h-3.5 w-3.5" /> Preview as Member</>}
            </button>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#f5c518] flex items-center justify-center text-[#0b0f18] font-bold text-sm">
              {user?.display_name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#f0f2f7] truncate">
                {user?.display_name || 'User'}
              </p>
              <p className="text-xs text-[#8a93a3] truncate">{user?.phone}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
          {/* Attribution + version */}
          <p className="text-[10px] text-[#8a93a3]/50 text-center mt-3 leading-tight">
            Software by <span className="text-[#8a93a3]/70 font-medium">@Sandeep Maheshwari</span>
          </p>
          <p className="text-[9px] text-[#8a93a3]/30 text-center mt-1 font-mono">{VERSION_LABEL}</p>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className={`lg:hidden sticky top-0 z-30 flex items-center justify-between h-14 px-4 border-b border-[#252a37] backdrop-blur ${
        forceMemberView ? 'bg-[rgba(245,197,24,0.08)]' : 'bg-[#06080d]/80'
      }`}>
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="mr-1">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 bg-[#10131c] border-r border-[#252a37]">
              <div className="flex items-center justify-between h-14 px-4 border-b border-[#252a37]">
                <span className="wordmark text-lg">BTCCP</span>
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <ScrollArea className="h-[calc(100vh-56px)] py-4">
                <SidebarNav onItemClick={() => setMobileOpen(false)} />
              </ScrollArea>
            </SheetContent>
          </Sheet>
          <span className="wordmark text-lg">BTCCP</span>
        </div>
        {/* Logout button always visible on mobile */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-[#8a93a3] hover:text-[#ef4444] hover:bg-transparent"
          data-testid="mobile-logout-button"
        >
          <LogOut className="h-4 w-4 mr-1.5" />
          Logout
        </Button>
      </div>

      {/* Main Content */}
      <main className="lg:pl-[260px]">
        <div className="min-h-[calc(100vh-56px)] lg:min-h-screen">
          <Outlet />
        </div>
        
        {/* Footer */}
        <footer className="py-4 px-6 text-center text-xs text-[#8a93a3] border-t border-[#252a37]">
          © Sandeep Maheshwari &nbsp;·&nbsp; <span className="font-mono">{VERSION_LABEL}</span>
        </footer>
      </main>
    </div>
  );
}
